import React, { useEffect, useState, useCallback } from 'react';
import ExpenseTable from '../components/ExpenseTable';
import CreditCardExpense from '../components/CreditCardExpense';
import SavingsTable from '../components/SavingsTable';
import { useSearchParams } from 'react-router-dom';

const BankStatement = ({ month: propMonth, year: propYear, defaultTab = 'debit' }) => {
  const [searchParams] = useSearchParams();
  // Use props if provided, otherwise fall back to URL params
  const urlMonth = propMonth || searchParams.get('month'); // Format expected: "YYYY-MM"
  const urlYear = propYear || searchParams.get('year');
  const urlTab = searchParams.get('tab'); // Get tab parameter from URL

  // Bank account types available for this company
  const [availableAccountTypes, setAvailableAccountTypes] = useState({ checking: false, credit: false, savings: false });
  const [accountTypesLoaded, setAccountTypesLoaded] = useState(false);

  // Fetch bank accounts to determine which tabs to show
  const fetchBankAccountTypes = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/company/banks`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      const banks = data.banks || [];

      const types = { checking: false, credit: false, savings: false };
      banks.forEach(bank => {
        const accountType = (bank.accountType || 'Checking').toLowerCase();
        if (accountType === 'checking' || accountType === 'debit') {
          types.checking = true;
        } else if (accountType === 'credit' || accountType === 'credit card') {
          types.credit = true;
        } else if (accountType === 'savings') {
          types.savings = true;
        }
      });

      setAvailableAccountTypes(types);
      setAccountTypesLoaded(true);
    } catch (error) {
      console.error('Error fetching bank account types:', error);
      // Default to showing all tabs if we can't determine
      setAvailableAccountTypes({ checking: true, credit: true, savings: true });
      setAccountTypesLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchBankAccountTypes();
  }, [fetchBankAccountTypes]);

  // Only use urlTab if it's a valid BankStatement tab, otherwise use defaultTab
  const getInitialTab = () => {
    if (urlTab && ['debit', 'credit', 'savings'].includes(urlTab)) {
      return urlTab;
    }
    return defaultTab;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // When account types are loaded, ensure active tab is valid
  useEffect(() => {
    if (accountTypesLoaded) {
      const tabTypeMap = { debit: 'checking', credit: 'credit', savings: 'savings' };
      const currentType = tabTypeMap[activeTab];
      if (currentType && !availableAccountTypes[currentType]) {
        // Current tab's account type doesn't exist, switch to first available
        if (availableAccountTypes.checking) setActiveTab('debit');
        else if (availableAccountTypes.credit) setActiveTab('credit');
        else if (availableAccountTypes.savings) setActiveTab('savings');
      }
    }
  }, [accountTypesLoaded, availableAccountTypes]);

  // Set up date filtering based on month param
  useEffect(() => {
    if (urlMonth && urlYear) {
      const year = parseInt(urlYear);
      const month = parseInt(urlMonth) - 1; // JS months are 0-indexed

      // First day of month
      const startDate = new Date(year, month, 1);
      // Last day of month
      const endDate = new Date(year, month + 1, 0);

      setFilterStartDate(formatDateForInput(startDate));
      setFilterEndDate(formatDateForInput(endDate));
    }
  }, [urlMonth, urlYear]);

  // Update active tab when URL parameter changes (only for valid BankStatement tabs)
  useEffect(() => {
    if (urlTab && ['debit', 'credit', 'savings'].includes(urlTab)) {
      setActiveTab(urlTab);
    }
  }, [urlTab]);

  // Update active tab when defaultTab prop changes
  useEffect(() => {
    if (defaultTab && ['debit', 'credit', 'savings'].includes(defaultTab)) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  // Helper function to format date for input fields (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  return (
    <div className='max-w-7xl mx-auto p-6 space-y-6'>
      <div className="card p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Bank Statement Management</h1>

        {filterStartDate && filterEndDate && (
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-50 text-primary-700 border border-primary-200">
            Showing transactions for: {new Date(filterStartDate).toLocaleDateString()} - {new Date(filterEndDate).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className='card p-6'>
        <div className='flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg'>
          {(availableAccountTypes.checking || !accountTypesLoaded) && (
            <button
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'debit'
                  ? 'bg-white text-gray-900 shadow-soft'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              onClick={() => setActiveTab('debit')}
            >
              Checking Account Transactions
            </button>
          )}
          {(availableAccountTypes.credit || !accountTypesLoaded) && (
            <button
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'credit'
                  ? 'bg-white text-gray-900 shadow-soft'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              onClick={() => setActiveTab('credit')}
            >
              Credit Card Transactions
            </button>
          )}
          {(availableAccountTypes.savings || !accountTypesLoaded) && (
            <button
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'savings'
                  ? 'bg-white text-gray-900 shadow-soft'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              onClick={() => setActiveTab('savings')}
            >
              Savings Account Transactions
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className='animate-fade-in'>
          {activeTab === 'debit' &&
            <ExpenseTable
              initialFilterStartDate={filterStartDate}
              initialFilterEndDate={filterEndDate}
            />
          }
          {activeTab === 'credit' &&
            <CreditCardExpense
              initialFilterStartDate={filterStartDate}
              initialFilterEndDate={filterEndDate}
            />
          }
          {activeTab === 'savings' &&
            <SavingsTable
              initialFilterStartDate={filterStartDate}
              initialFilterEndDate={filterEndDate}
            />
          }
        </div>
      </div>
    </div>
  );
};

export default BankStatement;
